import { Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { DEVICES } from '@constants/routes';

const messages = defineMessages({
  chart: {
    id: 'deviceAttribution.chart',
    defaultMessage:
      'This chart collates data provided by all your connected devices.',
  },
  table: {
    id: 'deviceAttribution.table',
    defaultMessage:
      'This table collates data provided by all the connected devices.',
  },
  devicesLink: {
    id: 'deviceAttribution.devicesLink',
    defaultMessage:
      'See {devices} to see the list of devices you have connected.',
  },
  devices: {
    id: 'devicesMain.myDevices',
    defaultMessage: 'My Health Devices',
  },
});

export interface DeviceDataAttributionProps {
  /** Which surface the line sits on. Changes the noun in the copy. */
  surface?: 'chart' | 'table';
  /**
   * Point the reader at their own device list. Off for shared records, where
   * the reader is not the patient and has no device list of their own.
   */
  showDevicesLink?: boolean;
  /**
   * Text colour token. Defaults to inheriting from the parent, so a caller
   * placing this on a dark header does not have to know the palette token.
   */
  color?: string;
}

/**
 * Attribution for views built from health device data.
 *
 * Garmin's brand guidelines require attribution wherever their data appears,
 * including anything derived from it, and reject tooltip or footnote
 * placement. Terra does not report a device model for Garmin, so the line
 * names the connected devices collectively rather than a model.
 *
 * Sits on the card carrying the data rather than under the page title, so it
 * reads as a description of what is on screen. That is a deliberate departure
 * from VEG-833 AC 3 and AC 4, which ask for it in both places.
 */
export default function DeviceDataAttribution({
  surface = 'chart',
  showDevicesLink = true,
  color = 'inherit',
}: DeviceDataAttributionProps) {
  const { formatMessage } = useIntl();

  return (
    <Typography color={color} variant="body2">
      {formatMessage(messages[surface])}
      {showDevicesLink ? (
        <>
          {' '}
          {formatMessage(messages.devicesLink, {
            devices: (
              <Link
                color="inherit"
                component={RouterLink}
                key="device-attribution-devices-link"
                to={DEVICES}
              >
                {formatMessage(messages.devices)}
              </Link>
            ),
          })}
        </>
      ) : null}
    </Typography>
  );
}
